import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useChat } from '../hooks/useChat';
import { websocketService } from '../api/services/websocketService';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

export default function Message() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    conversations,
    activeConversation,
    messages,
    loading,
    sending,
    messagesEndRef,
    selectConversation,
    sendMessage,
    deleteConversation,
  } = useChat();

  const { t } = useTranslation();
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const user = JSON.parse(userData);
      setCurrentUserId(user.id || user._id);
    }
    if (!websocketService.isConnected()) {
      websocketService.connect();
    }
    const unsubscribeConnection = websocketService.onConnection(() => {});
    const unsubscribeError = websocketService.onError(() => {});
    return () => {
      unsubscribeConnection();
      unsubscribeError();
    };
  }, []);

  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (conversationId && conversations.length > 0) {
      const conversation = conversations.find(c => c.id === conversationId);
      if (conversation && conversation.id !== activeConversation?.id) {
        selectConversation(conversation);
        setSearchParams({});
      }
    }
  }, [conversations, searchParams, activeConversation, selectConversation, setSearchParams]);

  useEffect(() => {
    if (messages.length > 0 && messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages, messagesEndRef]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim() || sending) return;
    await sendMessage(messageInput);
    setMessageInput('');
    if (inputRef.current) inputRef.current.focus();
  };

  const handleDeleteConversation = async () => {
    if (!activeConversation) return;
    if (window.confirm(t('messages.delete_confirm'))) {
      await deleteConversation(activeConversation.id);
    }
  };

  const formatTime = (dateString: string) => {
    const date = dayjs(dateString);
    const now = dayjs();
    const diffDays = now.diff(date, 'day');
    if (diffDays === 0) return date.format('h:mm A');
    if (diffDays === 1) return t('messages_page.yesterday');
    if (diffDays < 7) return date.format('ddd');
    return date.format('MMM D');
  };

  const filteredConversations = conversations.filter(
    conv =>
      conv.otherUser.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.listingTitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getDisplayName = (conversation: typeof activeConversation) => conversation ? conversation.otherUser.name : '';
  const getDisplayAvatar = (conversation: typeof activeConversation) => {
    if (!conversation) return 'https://i.pravatar.cc/40?img=1';
    return conversation.otherUser.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.otherUser.name)}&background=fe5a1d&color=fff`;
  };

  const isMyMessage = (message: { senderId: string }) => message.senderId === currentUserId;

  return (
    <div className="dash-messages-wrapper shadow-sm rounded-4 bg-white" style={{ height: 'calc(100vh - 140px)', display: 'flex', overflow: 'hidden', border: '1px solid var(--dash-border)' }}>
      {/* Sidebar List */}
      <div className="dash-messages-sidebar" style={{ width: 340, borderRight: '1px solid var(--dash-border)', display: 'flex', flexDirection: 'column' }}>
        <div className="p-3 border-bottom">
          <div className="dash-searchbar w-100 mb-0">
            <i className="bi bi-search text-muted"></i>
            <input type="text" placeholder={t('messages.search_placeholder', 'Search chats...')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto">
          {loading && conversations.length === 0 ? (
            <div className="text-center p-4 text-muted"><span className="spinner-border spinner-border-sm me-2"></span>Loading...</div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center p-4 text-muted small">{t('messages.no_conversations')}</div>
          ) : (
            filteredConversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => selectConversation(conv)}
                className={`dash-chat-item p-3 d-flex border-bottom ${activeConversation?.id === conv.id ? 'active' : ''}`}
                style={{ cursor: 'pointer', transition: 'background 0.2s', background: activeConversation?.id === conv.id ? 'rgba(254,90,29,0.06)' : 'transparent' }}
              >
                <img src={getDisplayAvatar(conv)} alt="user" className="rounded-circle me-3" width="48" height="48" style={{ objectFit: 'cover' }} />
                <div className="flex-grow-1 min-w-0">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <h6 className="fw-bold mb-0 text-truncate" style={{ fontSize: 14 }}>{conv.otherUser.name}</h6>
                    <small className="text-muted" style={{ fontSize: 11 }}>
                      {conv.lastMessage ? formatTime(conv.lastMessage.createdAt) : formatTime(conv.createdAt)}
                    </small>
                  </div>
                  <div className="text-muted text-truncate mb-1" style={{ fontSize: 12 }}>{conv.listingTitle}</div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-truncate text-muted" style={{ maxWidth: '80%' }}>
                      {conv.lastMessage?.content || ''}
                    </small>
                    {conv.unreadCount > 0 && (
                      <span className="badge rounded-pill bg-danger">{conv.unreadCount}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="dash-messages-area bg-light flex-grow-1 d-flex flex-column">
        {activeConversation ? (
          <>
            <div className="dash-chat-header bg-white p-3 border-bottom d-flex justify-content-between align-items-center flex-shrink-0">
              <div className="d-flex align-items-center">
                <img src={getDisplayAvatar(activeConversation)} alt="user" className="rounded-circle me-3" width="44" height="44" style={{ objectFit: 'cover' }} />
                <div>
                  <h6 className="fw-bold mb-0">{getDisplayName(activeConversation)}</h6>
                  <small className="text-primary fw-semibold">{activeConversation.listingTitle}</small>
                </div>
              </div>
              <button className="btn btn-sm btn-outline-danger" onClick={handleDeleteConversation}>
                <i className="bi bi-trash"></i>
              </button>
            </div>

            <div className="flex-grow-1 overflow-auto p-4 d-flex flex-column">
              {messages.length === 0 ? (
                <div className="m-auto text-center text-muted">
                  <i className="bi bi-chat-heart fs-1"></i>
                  <p className="mt-2">Send a message to start the conversation.</p>
                </div>
              ) : (
                messages.map((msg, i) => {
                  const isMine = isMyMessage(msg);
                  return (
                    <div key={msg.id} className={`d-flex mb-3 ${isMine ? 'justify-content-end' : 'justify-content-start'}`}>
                      <div className={`p-3 rounded-4 shadow-sm ${isMine ? 'bg-primary text-white' : 'bg-white text-dark'}`} style={{ maxWidth: '70%', background: isMine ? '#fe5a1d' : '#fff' }}>
                        <p className="mb-1" style={{ fontSize: 14 }}>{msg.content}</p>
                        <small className={isMine ? 'text-white-50' : 'text-muted'} style={{ fontSize: 11, display: 'block', textAlign: 'right' }}>
                          {dayjs(msg.createdAt).format('h:mm A')}
                        </small>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendMessage} className="bg-white p-3 border-top d-flex flex-shrink-0 gap-2">
              <input
                ref={inputRef}
                type="text"
                className="form-control rounded-pill bg-light border-0 px-4"
                placeholder={t('messages.input_placeholder')}
                value={messageInput}
                onChange={e => setMessageInput(e.target.value)}
                disabled={sending}
              />
              <button type="submit" className="btn btn-primary rounded-circle" style={{ width: 44, height: 44, padding: 0, background: '#fe5a1d', border: 'none' }} disabled={sending || !messageInput.trim()}>
                <i className="bi bi-send-fill text-white"></i>
              </button>
            </form>
          </>
        ) : (
          <div className="m-auto text-center text-muted">
            <i className="bi bi-chat-square-dots opacity-25" style={{ fontSize: 64 }}></i>
            <h5 className="mt-3 fw-bold">Select a conversation</h5>
            <p>Choose a listing conversation from the sidebar to view your messages here.</p>
          </div>
        )}
      </div>
    </div>
  );
}
